import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {Subscription} from 'rxjs';
import {CommonService} from "../../../../services/common.service";
import { formatNumber } from '@angular/common';

@Component({
  selector: 'app-number-input',
  templateUrl: './number-input.component.html',
  styleUrls: ['../../styles.css', './number-input.component.css']
})
export class NumberInputComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, AfterViewChecked, DoCheck {
  @ViewChild('input') input: ElementRef | undefined;

  @Input() label: string = '';
  @Input() focus: boolean = false;
  @Input() placeholder: string = '';
  @Input() parentFormGroup: FormGroup = new FormGroup<any>({});
  @Input() isDecimalAllowed:boolean=true;
  @Input() controlName: string = '';
  @Input() error: string = '';
  @Input() maxLength: string | number = 20;
  @Input() max: number = 9999999999;
  @Input() min: number = 0;
  @Input() prefixIcon: string = '';
  @Input() suffixIcon: string = '';
  @Input() type: string = '';
  @Input() textAlign: string;
  @Input() suffixText: string = '';
  @Input() prefixText: string = '';
  @Input() locale: string = 'en-US';
  @Input() showNumberSeparator: boolean = false;
  @Output() checkForErrors = new EventEmitter();

  fieldSubscription: Subscription | undefined;

  isRequired: boolean = false;
  isDisabled: boolean = false;
  private lastFormattedValue: string | null = null;

  constructor(
    private commonService: CommonService
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.['focus']?.currentValue) {
      const clientArea = $('#client_area');
      const y = clientArea.scrollTop() +
        this.input?.nativeElement.getBoundingClientRect().top -
        this.commonService.headerRemOffsetCalculator()

      clientArea.scrollTop(y);
    }
  }

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout', ['$event'])
  onBlur(event: Event) {
    if (!this.error) {
      this.parentFormGroup.controls[this.controlName].markAsUntouched();
    }
  }

  ngOnInit(): void {
    const control = this.parentFormGroup.controls[this.controlName];
    this.isDisabled = control.status === 'DISABLED';
    
    this.fieldSubscription = control.valueChanges.subscribe((value) => {
      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup.controls[this.controlName].touched) {
        this.parentFormGroup.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup.controls[this.controlName].errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control.validator) {
      const validator = control.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  ngAfterViewInit(): void {
    // Format initial value after view is initialized
    this.formatInitialValue();
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
  }

  onInputNumberFormat(event: any) {
    const input = event.target;
    const originalCursor = input.selectionStart;

    // Remove commas, but preserve decimal point if decimals are allowed
    let value = input.value.replace(/,/g, '');
    if (this.isDecimalAllowed) {
      // Allow digits and decimal point
      value = value.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
    } else {
      // Only allow digits
      value = value.replace(/[^\d]/g, '');
    }

    if (value === '') {
      this.parentFormGroup.controls[this.controlName].setValue(null, { emitEvent: false });
      input.value = '';
      return;
    }

    // Handle partial decimal input (e.g., "123." or ".")
    const isOnlyDecimal = value === '.';
    
    if (isOnlyDecimal) {
      // Allow just "." temporarily while user is typing
      input.value = value;
      return;
    }

    // Use parseFloat for decimals, parseInt for integers
    const numericValue = this.isDecimalAllowed ? parseFloat(value) : parseInt(value, 10);
    
    // Only set form value if it's a valid number (not just a decimal point)
    if (!isNaN(numericValue)) {
      this.parentFormGroup.controls[this.controlName].setValue(numericValue, { emitEvent: false });
    }

    if (this.showNumberSeparator) {
      input.value = formatNumber(numericValue, this.locale, '1.0-0');
      input.selectionStart = input.selectionEnd = originalCursor + (input.value.length - value.length);
    } else {
      // Preserve the value as-is, including trailing decimal point
      input.value = value;
    }
  }

  onBlurNumberFormat(event: any) {
    const value = this.parentFormGroup.controls[this.controlName].value;
    if (value != null && this.showNumberSeparator) {
      event.target.value = formatNumber(value, this.locale, '1.0-0');
    }
  }

  /**
   * Format the initial value when component loads
   */
  private formatInitialValue(): void {
    if (this.input?.nativeElement && this.showNumberSeparator) {
      const value = this.parentFormGroup.controls[this.controlName].value;
      if (value != null && this.showNumberSeparator) {
        this.input.nativeElement.value = formatNumber(value, this.locale, '1.0-0');
      }
    }
  }


  ngAfterViewChecked() {
    const control = this.parentFormGroup.controls[this.controlName];
    if (control?.value != null && this.showNumberSeparator && this.input?.nativeElement) {
      const formatted = formatNumber(control.value, this.locale, '1.0-0');
      // Only update DOM if value changed (prevents flicker)
      if (formatted !== this.lastFormattedValue) {
        this.input.nativeElement.value = formatted;
        this.lastFormattedValue = formatted;
      }
    }
  }

  ngDoCheck() {
    // Detect manual changes (patchValue with emitEvent: false)
    this.formatInitialValue();
  }
}
